// A batcher: items queue up and leave in batches of at most `maxSize`, when a batch is full or the oldest queued item
// has waited `maxAgeMs`, whichever comes first. A failed send is retried with exponential backoff up to `attempts`
// times, then dropped (counted). A send that rejects with a "wait" error (the backend isn't ready yet: signed out,
// offline) keeps the batch without spending an attempt. The queue is capped: past `maxQueue` the oldest items go.
// Order is kept: a retried batch goes back to the front. Timers are injected, so the tests run on a fake clock.

/** Timer functions (the platform's by default). */
export interface Timers {
  set(fn: () => void, ms: number): unknown;
  clear(handle: unknown): void;
}

/** The platform's timers. */
export const realTimers: Timers = {
  set: (fn, ms) => setTimeout(fn, ms),
  clear: (h) => clearTimeout(h as ReturnType<typeof setTimeout>),
};

/** Thrown (or rejected) by a send that can't go yet; the batch waits for the next flush without using an attempt. */
export class NotReadyError extends Error {
  /** Marks the error for the batcher (instanceof can fail across bundles). */
  readonly wait = true;
  constructor(message: string) {
    super(message);
    this.name = "NotReadyError";
  }
}

/** Is this a {@link NotReadyError} (or anything marked `wait`)? */
export const isNotReady = (e: unknown): boolean => typeof e === "object" && e !== null && (e as { wait?: unknown }).wait === true;

/** Batching limits. */
export interface BatchLimits {
  /** Most items in one send. */
  maxSize: number;
  /** Longest an item waits before its batch is sent, ms. */
  maxAgeMs: number;
  /** Most items held (oldest dropped past it), so an offline session can't grow without bound. */
  maxQueue: number;
  /** Sends tried for one batch before it is dropped (1: no retry). */
  attempts: number;
  /** First retry delay, ms; doubled each attempt. */
  retryBaseMs: number;
  /** Longest retry delay, ms. */
  retryMaxMs: number;
}

/** Options for {@link createBatcher}. */
export interface BatcherOptions<T> extends BatchLimits {
  send(batch: T[]): Promise<void>;
  timers?: Timers;
  /** Told about items dropped: "overflow" (past maxQueue), "failed" (out of attempts), "cleared". */
  onDrop?(items: T[], reason: "overflow" | "failed" | "cleared", error?: unknown): void;
}

/** What a batcher has done. */
export interface BatcherStats {
  queued: number;
  sent: number;
  batches: number;
  retries: number;
  waits: number;
  dropped: number;
  lastError: string | null;
}

/** A batcher; see the file's comment. */
export interface Batcher<T> {
  add(item: T): void;
  /** Send what is queued now (in batches), ignoring the age limit. Resolves when the queue is empty or a send failed. */
  flush(): Promise<void>;
  /** Drop everything queued (an opt-out). */
  clear(): void;
  stats(): BatcherStats;
  /** Stop the timers. */
  dispose(): void;
}

/** Make a batcher. */
export function createBatcher<T>(o: BatcherOptions<T>): Batcher<T> {
  const timers = o.timers ?? realTimers;
  const maxSize = Math.max(1, Math.floor(o.maxSize));
  const queue: T[] = [];
  const stats: BatcherStats = { queued: 0, sent: 0, batches: 0, retries: 0, waits: 0, dropped: 0, lastError: null };
  let timer: unknown = null;
  let inFlight: Promise<void> | null = null;
  let failures = 0; // attempts spent on the batch at the front
  let disposed = false;

  const arm = (ms: number): void => {
    if (timer !== null || disposed) return;
    timer = timers.set(() => {
      timer = null;
      void flush();
    }, ms);
  };
  const disarm = (): void => {
    if (timer !== null) timers.clear(timer);
    timer = null;
  };
  const drop = (items: T[], reason: "overflow" | "failed" | "cleared", error?: unknown): void => {
    if (!items.length) return;
    stats.dropped += items.length;
    o.onDrop?.(items, reason, error);
  };

  const sendOne = async (): Promise<boolean> => {
    const batch = queue.splice(0, maxSize);
    if (!batch.length) return false;
    try {
      await o.send(batch);
      stats.sent += batch.length;
      stats.batches++;
      failures = 0;
      return true;
    } catch (e) {
      stats.lastError = e instanceof Error ? e.message : String(e);
      if (isNotReady(e)) {
        stats.waits++;
        queue.unshift(...batch);
        arm(o.maxAgeMs);
        return false;
      }
      failures++;
      if (failures < o.attempts) {
        stats.retries++;
        queue.unshift(...batch);
        arm(Math.min(o.retryMaxMs, o.retryBaseMs * 2 ** (failures - 1)));
      } else {
        failures = 0;
        drop(batch, "failed", e);
        if (queue.length) arm(o.maxAgeMs);
      }
      return false;
    }
  };

  const flush = (): Promise<void> => {
    if (inFlight) return inFlight;
    disarm();
    const run = async (): Promise<void> => {
      while (queue.length && (await sendOne()));
    };
    // cleared in a `finally` callback, which always runs after this assignment (an empty queue finishes `run` at once)
    const p: Promise<void> = run().finally(() => {
      if (inFlight === p) inFlight = null;
      if (queue.length && timer === null) arm(o.maxAgeMs);
    });
    inFlight = p;
    return p;
  };

  return {
    add(item) {
      if (disposed) return;
      queue.push(item);
      if (queue.length > o.maxQueue) drop(queue.splice(0, queue.length - o.maxQueue), "overflow");
      if (queue.length >= maxSize && !inFlight && failures === 0) void flush();
      else arm(o.maxAgeMs);
    },
    flush,
    clear() {
      disarm();
      failures = 0;
      drop(queue.splice(0), "cleared");
    },
    stats: () => ({ ...stats, queued: queue.length }),
    dispose() {
      disposed = true;
      disarm();
    },
  };
}
